import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2, Eye, EyeOff, Upload, Video, Loader2 } from 'lucide-react';

interface Testimonial {
  id: string;
  name: string;
  team: string;
  quote: string;
  sort_order: number;
  visible: boolean;
}

interface TestimonialVideo {
  id: string;
  video_url: string;
  title: string;
  sort_order: number;
  visible: boolean;
  created_at: string;
}

const TestimonialsAdmin = () => {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [newItem, setNewItem] = useState({ name: '', team: '', quote: '' });
  const [videos, setVideos] = useState<TestimonialVideo[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchItems = async () => {
    const { data } = await supabase.from('testimonials').select('*').order('sort_order');
    if (data) setItems(data);
  };

  const fetchVideos = async () => {
    const { data } = await supabase.from('testimonial_videos').select('*').order('sort_order');
    if (data) setVideos(data);
  };

  useEffect(() => { fetchItems(); fetchVideos(); }, []);

  const addItem = async () => {
    if (!newItem.name || !newItem.quote) return toast.error('Vyplňte jméno a citát.');
    const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) + 1 : 0;
    const { error } = await supabase.from('testimonials').insert({ ...newItem, sort_order: maxOrder });
    if (error) return toast.error(error.message);
    toast.success('Přidáno!');
    setNewItem({ name: '', team: '', quote: '' });
    fetchItems();
  };

  const updateItem = async (id: string, updates: Partial<Testimonial>) => {
    await supabase.from('testimonials').update(updates).eq('id', id);
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i));
  };

  const deleteItem = async (id: string) => {
    await supabase.from('testimonials').delete().eq('id', id);
    toast.success('Smazáno');
    fetchItems();
  };

  // Video management
  const uploadVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) return toast.error('Vyberte video soubor.');

    setUploading(true);
    const ext = file.name.split('.').pop();
    const path = `${crypto.randomUUID()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('testimonial-videos')
      .upload(path, file);

    if (uploadError) {
      toast.error('Chyba při nahrávání: ' + uploadError.message);
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage
      .from('testimonial-videos')
      .getPublicUrl(path);

    const maxOrder = videos.length > 0 ? Math.max(...videos.map(v => v.sort_order)) + 1 : 0;
    const { error: dbError } = await supabase.from('testimonial_videos').insert({
      video_url: urlData.publicUrl,
      title: file.name.replace(/\.[^/.]+$/, ''),
      sort_order: maxOrder,
    });

    if (dbError) toast.error(dbError.message);
    else toast.success('Video nahráno!');

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    fetchVideos();
  };

  const toggleVideoVisibility = async (id: string, visible: boolean) => {
    await supabase.from('testimonial_videos').update({ visible: !visible }).eq('id', id);
    setVideos(prev => prev.map(v => v.id === id ? { ...v, visible: !visible } : v));
  };

  const deleteVideo = async (v: TestimonialVideo) => {
    // Extract path from URL
    const urlParts = v.video_url.split('/testimonial-videos/');
    if (urlParts[1]) {
      await supabase.storage.from('testimonial-videos').remove([urlParts[1]]);
    }
    await supabase.from('testimonial_videos').delete().eq('id', v.id);
    toast.success('Video smazáno');
    fetchVideos();
  };

  return (
    <div>
      <h1 className="text-4xl tracking-wider text-foreground mb-8" style={{ fontFamily: 'Bebas Neue' }}>
        Reference
      </h1>

      {/* VIDEO SECTION */}
      <div className="mb-12">
        <h2 className="text-2xl tracking-wider text-foreground mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue' }}>
          <Video size={22} className="text-primary" /> Video reference
        </h2>

        <div className="p-4 bg-card border border-border rounded-md mb-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={uploadVideo}
            className="hidden"
          />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {uploading ? 'Nahrávám...' : 'Nahrát video'}
          </Button>
        </div>

        <div className="space-y-3">
          {videos.map((v) => (
            <div key={v.id} className="flex items-center gap-4 p-4 bg-card border border-border rounded-md">
              <video src={v.video_url} className="w-32 h-20 object-cover rounded" preload="metadata" />
              <div className="flex-1">
                <Input
                  value={v.title}
                  onChange={async (e) => {
                    const title = e.target.value;
                    await supabase.from('testimonial_videos').update({ title }).eq('id', v.id);
                    setVideos(prev => prev.map(x => x.id === v.id ? { ...x, title } : x));
                  }}
                  className="bg-background border-border text-sm"
                  placeholder="Název videa"
                />
              </div>
              <button onClick={() => toggleVideoVisibility(v.id, v.visible)} className={`p-2 ${v.visible ? 'text-primary' : 'text-muted-foreground'}`}>
                {v.visible ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
              <button onClick={() => deleteVideo(v)} className="p-2 text-muted-foreground hover:text-destructive">
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* TEXT TESTIMONIALS SECTION */}
      <h2 className="text-2xl tracking-wider text-foreground mb-4" style={{ fontFamily: 'Bebas Neue' }}>
        Textové reference
      </h2>

      <div className="p-4 bg-card border border-border rounded-md mb-8 space-y-3">
        <h3 className="text-sm font-medium text-foreground">Přidat novou referenci</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input placeholder="Jméno" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="bg-background border-border" />
          <Input placeholder="Tým / Klub" value={newItem.team} onChange={(e) => setNewItem({ ...newItem, team: e.target.value })} className="bg-background border-border" />
        </div>
        <Textarea placeholder="Citát" value={newItem.quote} onChange={(e) => setNewItem({ ...newItem, quote: e.target.value })} className="bg-background border-border" rows={2} />
        <Button onClick={addItem}><Plus size={16} /> Přidat</Button>
      </div>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="flex items-start gap-4 p-4 bg-card border border-border rounded-md">
            <div className="flex-1 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input value={item.name} onChange={(e) => updateItem(item.id, { name: e.target.value })} className="bg-background border-border text-sm" />
                <Input value={item.team || ''} onChange={(e) => updateItem(item.id, { team: e.target.value })} className="bg-background border-border text-sm" />
              </div>
              <Textarea value={item.quote} onChange={(e) => updateItem(item.id, { quote: e.target.value })} className="bg-background border-border text-sm" rows={2} />
            </div>
            <button onClick={() => updateItem(item.id, { visible: !item.visible })} className={`p-2 ${item.visible ? 'text-primary' : 'text-muted-foreground'}`}>
              {item.visible ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>
            <button onClick={() => deleteItem(item.id)} className="p-2 text-muted-foreground hover:text-destructive">
              <Trash2 size={18} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TestimonialsAdmin;
